import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "api/axios";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PageShell, { SectionCard } from "components/patch/PageShell";
import PayrollRunPicker from "components/patch/PayrollRunPicker";

const money = (value) =>
  Number(value || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function PayrollSummary() {
  const [summary, setSummary] = useState(null);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (payload) => {
    const runId = payload?.runId || payload?.id || payload?.payroll_run_id;
    const month = payload?.month || payload?.value || payload?.payroll_month || payload;

    setLoading(true);
    setError("");
    setSummary(null);

    try {
      const params = { per_page: 1 };

      if (runId) {
        params.run_id = runId;
      } else if (month) {
        params.month = month;
      } else {
        setError("Please select a valid payroll run.");
        setLoading(false);
        return;
      }

      const res = await axios.get("/reports/payroll-detail", { params });

      const data = res.data?.data || {};

      setRun(data.run || null);
      setSummary(data.summary || null);
    } catch (err) {
      console.error("Grand Summary Error:", err);
      setError(err.response?.data?.message || "Unable to load payroll summary.");
    } finally {
      setLoading(false);
    }
  };

  const cards = [
    ["Staff Count", summary?.staff_count, false],
    ["Total Gross", summary?.total_gross, true],
    ["Total Deductions", summary?.total_deductions, true],
    ["Total Net", summary?.total_net, true],
  ];

  return (
    <PageShell title="Grand Summary" subtitle="Load a payroll run and review the payment summary.">
      <SectionCard title="Pick Payroll Run">
        <PayrollRunPicker onSelected={load} />
      </SectionCard>

      {loading && (
        <MDBox mt={3} display="flex" alignItems="center" gap={2}>
          <CircularProgress size={24} />
          <MDTypography variant="button">Loading payroll summary...</MDTypography>
        </MDBox>
      )}

      {error && (
        <MDBox mt={3}>
          <Alert severity="error">{error}</Alert>
        </MDBox>
      )}

      {summary && (
        <MDBox mt={3}>
          {run && (
            <MDBox mb={2}>
              <MDTypography variant="button" color="text">
                Selected Run: {run.month} | Status: {run.status || "N/A"}
              </MDTypography>
            </MDBox>
          )}

          <Grid container spacing={3}>
            {cards.map(([label, value, isMoney]) => (
              <Grid item xs={12} sm={6} md={3} key={label}>
                <Card sx={{ borderRadius: 3, height: "100%" }}>
                  <MDBox p={3}>
                    <MDTypography variant="button" color="text">
                      {label}
                    </MDTypography>

                    <MDTypography variant="h4" fontWeight="bold">
                      {isMoney ? `₦${money(value)}` : value || 0}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        </MDBox>
      )}
    </PageShell>
  );
}
